import { FeatureFlagsConfig } from "./types";

// use for pure js functions, and within react components use useFeatureFlag
export function isFeatureFlagEnabled(
  config: FeatureFlagsConfig | undefined | null,
  path: string,
): boolean {
  // no config or it's blank → all feature flags enabled
  if (!config || Object.keys(config).length === 0) return true;

  const keys = path.split("."); // 'sites.settings.crawler' → ['sites','settings','crawler']
  let current: true | false | FeatureFlagsConfig = config;

  for (const key of keys) {
    // if current level is NON object → deny
    if (typeof current !== "object" || current === null) {
      return false;
    }

    // key is not inside current → deny
    if (!(key in current)) {
      return false;
    }

    const value: true | false | FeatureFlagsConfig = current[key];

    if (value === false) {
      return false;
    }

    current = value;
  }

  // all keys passed → allow
  return true;
}

type FeatureFlagValue = true | false | FeatureFlagsConfig;

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

export function isEmptyConfig(
  config: FeatureFlagsConfig | null | undefined,
): boolean {
  return !config || Object.keys(config).length === 0;
}

export function hasObjectKeys(value: unknown): boolean {
  return isPlainObject(value) && Object.keys(value).length > 0;
}

/**
 * Merge user feature config into base config.
 *
 * Base config can contain both true/false (allow/deny).
 * User config is treated ONLY as restrictions:
 * - respect ONLY `false`
 * - ignore `true`
 * - recursively apply nested objects
 */
export function mergeUserConfig(
  baseConfig: FeatureFlagsConfig | null | undefined,
  userConfig: unknown,
): FeatureFlagsConfig | null | undefined {
  if (!userConfig || !isPlainObject(userConfig)) return baseConfig;

  // if base is non-object (true/false/null/undefined), normalize to object
  const result: FeatureFlagsConfig = isPlainObject(baseConfig)
    ? { ...baseConfig }
    : {};

  for (const key of Object.keys(userConfig)) {
    const userValue = userConfig[key];

    // user can only deny
    if (userValue === false) {
      result[key] = false;
      continue;
    }

    // ignore truthy keys
    if (userValue === true) continue;

    // nested object
    if (isPlainObject(userValue)) {
      const baseValue: FeatureFlagValue | undefined = result[key];

      // if base already denies this key, keep it denied
      if (baseValue === false) continue;

      // normalize base branch to object
      // - if base is object -> keep
      // - if base is true/undefined/null -> treat as {}
      const normalizedBaseBranch = isPlainObject(baseValue) ? baseValue : {};

      const mergedBranch = mergeUserConfig(normalizedBaseBranch, userValue);

      if (mergedBranch && isPlainObject(mergedBranch)) {
        result[key] = mergedBranch;
      }
    }
  }

  return result;
}
